import { html, LitElement, type PropertyValues } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { createRef, type Ref, ref } from "lit/directives/ref.js";
import { ifDefined } from "lit/directives/if-defined.js";

import "ui-components/expandible-switch/expandible-switch";
import type { FileCropper } from "ui-components/file-cropper";
import type { WowModal } from "ui-components/modal/wow-modal";
import "ui-components/premium-content-controls/premium-content-controls";
import { featureFlag } from "ui-components/wow-feature/wow-feature";

import { newCreateLinkModalStyles } from "./new-create-link-modal.css";
import { featureFlagsQueryOptions } from "../../queries/feature-flags";

import type { CreateUnifiedLinkParams } from "api-contracts/rpc/create-unified-link";
import type { GetUnifiedLinkResult } from "api-contracts/rpc/get-unified-link";
import type { GetMySubscriptionTierResult } from "api-contracts/rpc/get-my-subscription-tier";
import type { UpdateUnifiedLinkParams } from "api-contracts/rpc/update-unified-link";
import { DataQueryController } from "ui-common/data-query-controller";
import { combineDateAndTime, splitIsoToDateAndTime } from "helpers/dateHelpers";
import { validateUrl } from "helpers/url";
import { base64ToFile } from "ui-common/services/file-upload";
import { rpc } from "ui-common/services/rpc";
import { getMySubscriptionTierQueryOptions } from "ui-common/queries/subscription-tier";
import { NotificationService } from "ui-components/notification";
import { createUnifiedLink, updateUnifiedLink } from "../../services/unified-link-mutation";
import { t, translate } from "../../translations/i18n";
import type { WowInput } from "ui-components/form-elements/input/wow-input";

const MAX_TITLE_LENGTH = 50;

// TODO: implementation of brand is pending for discount

@customElement("new-create-link-modal")
export class NewCreateLinkModal extends LitElement {
  static override styles = [newCreateLinkModalStyles];

  @property({ type: String })
  linkId?: string;

  @state()
  private isSubmitting = false;

  @state()
  private urlEnrichingStatus: "not_done" | "in_progress" | "done" = "not_done";

  @state()
  private isUrlValid = false;

  @state()
  private hasUrlBeenTouched = false;

  // Manual form state
  @state() private linkUrl = "";
  @state() private linkTitle = "";
  @state() private code = "";
  @state() private teaser = "";
  @state() private startDate = "";
  @state() private startTime = "";
  @state() private endDate = "";
  @state() private endTime = "";
  @state() private fileId = "";

  // Premium content state
  @state() private premiumTier: "free" | "premium" = "free";
  @state() private enableOneTimePurchase = false;
  @state() private oneTimePriceCents?: number;

  // UI state - not part of form data
  @state()
  private switches = {
    discount: false,
  };

  @state() private isFileUploadActive = false;

  @state()
  private isFormInitialized = false;

  @state()
  linkData?: GetUnifiedLinkResult;

  @state()
  private fetchingEditLinkData = false;

  private get isEditMode() {
    return !!this.linkId;
  }

  fileCropperRef: Ref<FileCropper> = createRef();

  private subscriptionTierQuery = new DataQueryController<GetMySubscriptionTierResult>(this, () =>
    getMySubscriptionTierQueryOptions(),
  );

  private featureFlagsQuery = new DataQueryController(this, () => featureFlagsQueryOptions);

  private urlEnrichmentAbortController: AbortController | null = null;

  override async connectedCallback(): Promise<void> {
    super.connectedCallback();
    this.initializeFormData();
    this.getLinkData();
  }

  protected override willUpdate(changedProperties: PropertyValues): void {
    // Re-initialize form data when linkId changes
    if (changedProperties.has("linkId")) {
      this.initializeFormData();
    }
  }

  protected override updated(changedProperties: PropertyValues): void {
    super.updated(changedProperties);

    if (!this.isFormInitialized && this.linkData) {
      this.populateForm();
      this.isFormInitialized = true;
    }
  }

  private initializeFormData() {
    if (this.isEditMode) {
      // Reset URL validation state for edit mode - valid state
      this.urlEnrichingStatus = "done";
      this.isUrlValid = true;
      this.hasUrlBeenTouched = true; // Mark as touched for edit mode
    } else {
      // Reset URL validation state for new links - neutral state
      this.isUrlValid = true; // Start as valid until proven otherwise
      this.hasUrlBeenTouched = false;
    }

    // Initialize form data from linkData
    this.linkTitle = this.linkData?.title || "";
    this.linkUrl = this.linkData?.url || "";
    this.fileId = this.linkData?.promotion_file?.id || "";
    this.code = this.linkData?.code || "";
    this.teaser = this.linkData?.teaser || "";

    // Initialize premium data
    this.premiumTier = this.linkData?.contentTier || "free";
    if (this.linkData?.oneTimeProduct) {
      this.enableOneTimePurchase = true;
      this.oneTimePriceCents = this.linkData.oneTimeProduct.priceCents;
    } else {
      this.enableOneTimePurchase = false;
      this.oneTimePriceCents = undefined;
    }

    if (this.linkData?.start_date) {
      const { date, time } = splitIsoToDateAndTime(this.linkData.start_date);
      this.startDate = date;
      this.startTime = time;
    } else {
      this.startDate = "";
      this.startTime = "";
    }

    if (this.linkData?.end_date) {
      const { date, time } = splitIsoToDateAndTime(this.linkData.end_date);
      this.endDate = date;
      this.endTime = time;
    } else {
      this.endDate = "";
      this.endTime = "";
    }
  }

  private populateForm() {
    if (this.linkData?.has_discount) {
      this.switches = { ...this.switches, discount: true };
    }
  }

  private handleTierChange = (event: CustomEvent) => {
    this.premiumTier = event.detail.tier;
  };

  private handleOneTimePurchaseChange = (event: CustomEvent) => {
    const { enabled, priceCents } = event.detail;
    this.enableOneTimePurchase = enabled;
    this.oneTimePriceCents = priceCents;
  };

  private async validateUrl() {
    if (!this.linkUrl || this.linkUrl.trim().length === 0) {
      this.isUrlValid = false;
      return;
    }

    // For validation, add protocol if missing
    let urlToValidate = this.linkUrl.trim();
    if (!urlToValidate.match(/^https?:\/\//i)) {
      urlToValidate = "https://" + urlToValidate;
    }

    this.isUrlValid = validateUrl(urlToValidate);

    const input = this.renderRoot.querySelector<WowInput>("#url");
    input?.setCustomValidity(this.isUrlValid ? "" : "Invalid url");
  }

  /**
   * Check if non-URL form fields are empty
   * Used to determine if URL enrichment should run
   */
  private isFormEmpty(): boolean {
    return (
      !this.linkTitle.trim() &&
      !this.code.trim() &&
      !this.teaser.trim() &&
      !this.startDate.trim() &&
      !this.endDate.trim() &&
      !this.fileId.trim()
    );
  }

  private handleUrlChange = (event: Event) => {
    const target = event.target as HTMLInputElement;
    this.hasUrlBeenTouched = true;

    // Start enrichment if URL is valid and form is empty
    setTimeout(() => {
      if (target && target.value && this.isUrlValid && this.isFormEmpty()) {
        this.enrichUrl(target.value.trim());
      }
    }, 0);
  };

  private handleUrlInput = (event: Event) => {
    const target = event.target as HTMLInputElement;
    this.linkUrl = target.value;

    // Validate URL on input after it's been touched
    if (this.hasUrlBeenTouched) {
      this.validateUrl();
    }
  };

  private handleUrlPaste = (event: ClipboardEvent) => {
    const pastedText = event.clipboardData?.getData("text");
    if (pastedText && pastedText.match(/^https?:\/\//i)) {
      // If pasted text has protocol, let it paste normally
      // The form controller will handle the value update
    }
    // Otherwise, let default paste behavior happen
  };

  private handleUrlBlur = (event: FocusEvent) => {
    const target = event.target as HTMLInputElement;
    let value = target.value.trim();

    // Add https:// if no protocol present
    if (value && !value.match(/^https?:\/\//i)) {
      value = "https://" + value;
      target.value = value;
      // Update our manual state
      this.linkUrl = value;
    }

    if (value && this.isUrlValid && this.isFormEmpty()) {
      this.enrichUrl(value);
    }
  };

  private async enrichUrl(value: string) {
    try {
      // Cancel previous request if in progress
      if (this.urlEnrichmentAbortController) {
        this.urlEnrichmentAbortController.abort();
      }

      // Skip if already in progress to avoid multiple concurrent requests
      if (this.urlEnrichingStatus === "in_progress") return undefined;

      // Create new abort controller for this request
      this.urlEnrichmentAbortController = new AbortController();

      this.urlEnrichingStatus = "in_progress";

      const res = await rpc("urlEnricher", { url: value });
      const { title, imageFile } = res;

      // Only proceed if request wasn't aborted
      if (!this.urlEnrichmentAbortController?.signal.aborted) {
        // ALWAYS set status to done after successful response
        this.urlEnrichingStatus = "done";

        // Force re-render to update skeleton state
        this.requestUpdate();

        if (title) {
          // Update title regardless of image
          setTimeout(() => {
            this.linkTitle = title;
          });
        }

        if (imageFile) {
          const file = base64ToFile(imageFile, "ogImage.png");
          setTimeout(() => {
            this.fileCropperRef.value?.loadImage(file);
          });
        }
      }
    } catch {
      // Only update status if request wasn't aborted
      if (!this.urlEnrichmentAbortController?.signal.aborted) {
        this.urlEnrichingStatus = "not_done";

        // Show notification that preview is not available
        NotificationService.show({
          message: "No preview available for this link. You can still create the link manually.",
          severity: "info",
          duration: 4000,
        });
      }
    } finally {
      // Clean up abort controller
      if (this.urlEnrichmentAbortController && !this.urlEnrichmentAbortController.signal.aborted) {
        this.urlEnrichmentAbortController = null;
      }
    }
  }

  private async getLinkData() {
    if (!this.linkId) return;

    try {
      this.fetchingEditLinkData = true;
      this.linkData = await rpc("getUnifiedLink", { linkId: this.linkId });

      // Re-initialize form data when link data fetched
      this.initializeFormData();
      // Set switches based on link data
      this.switches = {
        discount: !!this.linkData.has_discount,
      };
    } catch (error) {
      console.error(`Error getting link data: ${error}`);
    } finally {
      this.fetchingEditLinkData = false;
    }
  }

  private validateForm(): boolean {
    let valid = true;

    const urlInput = this.renderRoot.querySelector<WowInput>("#url");
    const titleInput = this.renderRoot.querySelector<WowInput>('wow-input[name="linkTitle"]');
    const startDateInput = this.renderRoot.querySelector<WowInput>('wow-input[name="startDate"]');
    const endDateInput = this.renderRoot.querySelector<WowInput>('wow-input[name="endDate"]');

    urlInput?.setCustomValidity("");
    titleInput?.setCustomValidity("");
    startDateInput?.setCustomValidity("");
    endDateInput?.setCustomValidity("");

    // URL validation
    if (!this.linkUrl.trim()) {
      urlInput?.setCustomValidity("URL is required.");
      valid = false;
    } else {
      let urlToValidate = this.linkUrl.trim();
      if (!urlToValidate.match(/^https?:\/\//i)) urlToValidate = "https://" + urlToValidate;
      this.isUrlValid = validateUrl(urlToValidate);
      urlInput?.setCustomValidity(this.isUrlValid ? "" : "Please enter a valid URL (e.g., https://example.com)");
      if (!this.isUrlValid) valid = false;
    }

    // Title validation
    if (!this.linkTitle.trim()) {
      titleInput?.setCustomValidity("Title is required.");
      valid = false;
    } else if (this.linkTitle.trim().length > MAX_TITLE_LENGTH) {
      titleInput?.setCustomValidity(`Title can contain up to ${MAX_TITLE_LENGTH} characters.`);
      valid = false;
    }
    {
      titleInput?.setCustomValidity("");
    }

    // Discount date validation
    if (this.switches.discount) {
      if (!this.startDate) {
        startDateInput?.setCustomValidity("Start date is required for discounts.");
        valid = false;
      } else {
        startDateInput?.setCustomValidity("");
      }

      if (this.startDate && this.endDate) {
        const startDateTime = combineDateAndTime(this.startDate, this.startTime);
        const endDateTime = combineDateAndTime(this.endDate, this.endTime, { endOfDay: true });
        if (endDateTime <= startDateTime) {
          endDateInput?.setCustomValidity("End date must be after start date");
          valid = false;
        } else {
          endDateInput?.setCustomValidity("");
        }
      }
    }

    // Force UI to show validation hints
    urlInput?.reportValidity();
    titleInput?.reportValidity();
    startDateInput?.reportValidity();
    endDateInput?.reportValidity();

    return valid;
  }

  private ensureUrlProtocol(url: string): string {
    const trimmed = url.trim();
    if (!trimmed) return trimmed;

    if (!trimmed.match(/^https?:\/\//i)) {
      return "https://" + trimmed;
    }
    return trimmed;
  }

  private async handleLinkSave() {
    this.isSubmitting = true;
    const hasDiscount = this.switches.discount;

    try {
      const startDateTime = this.startDate ? combineDateAndTime(this.startDate, this.startTime) : undefined;

      const endDateTime = this.endDate ? combineDateAndTime(this.endDate, this.endTime, { endOfDay: true }) : undefined;

      // Ensure URL has protocol before sending to backend
      const normalizedUrl = this.ensureUrlProtocol(this.linkUrl);

      const linkParams = {
        // Basic link fields
        title: this.linkTitle.trim(),
        url: normalizedUrl,
        teaser: this.teaser.trim(),
        fileId: this.fileId || undefined,

        // Discount Specific fields
        has_discount: hasDiscount,
        ...(hasDiscount && {
          is_code_required: !!this.code.trim(),
          code: this.code.trim() || undefined,
          start_date: startDateTime ? startDateTime.toISOString() : undefined,
          end_date: endDateTime ? endDateTime.toISOString() : undefined,
          published_at: new Date().toISOString(),
        }),

        // Premium content fields
        contentTier: this.premiumTier,
        oneTimePriceCents: this.enableOneTimePurchase ? this.oneTimePriceCents : undefined,
      };

      if (this.isEditMode && this.linkId) {
        // Update link
        const fileId = this.linkData?.promotion_file?.id === this.fileId ? undefined : this.fileId;
        const updateLinkParams: UpdateUnifiedLinkParams = {
          ...linkParams,
          linkId: this.linkId,
          fileId: fileId,
        };
        await updateUnifiedLink.mutate(updateLinkParams);
      } else {
        // Create new link
        const createLinkParams: CreateUnifiedLinkParams = linkParams;
        await createUnifiedLink.mutate(createLinkParams);
      }

      this.isSubmitting = false;

      const message = this.isEditMode
        ? translate("editLink.linkUpdatedSuccess", { title: this.linkTitle })
        : translate("createLink.linkCreatedSuccess", { title: this.linkTitle });

      // Show success notification
      NotificationService.show({ message, severity: "success" });

      // Clear form data after successful save so modal can close
      this.clearFormData();

      // Close Modal after successful
      this.closeModal();

      // Dispatch event to notify parent components that a link was created
      this.dispatchEvent(
        new CustomEvent("link-created", {
          bubbles: true,
          composed: true,
        }),
      );
    } catch (error: unknown) {
      console.error(`Error ${this.isEditMode ? "updating" : "creating"} link:`, error);
      NotificationService.show({
        message: translate(this.isEditMode ? "editLink.updateError" : "createLink.createError"),
        severity: "error",
      });
    }
  }

  private handleImageUpload = (e: CustomEvent) => {
    if (!e.detail) {
      return;
    }
    const { fileId } = e.detail;

    // Update form state with file ID
    this.fileId = fileId;
    this.isFileUploadActive = false;
  };

  private handleFileUploadStart = () => {
    this.isFileUploadActive = true;
  };

  private handleFileUploadCancel = () => {
    this.isFileUploadActive = false;
  };

  private clearFormData(): void {
    this.linkTitle = "";
    this.linkUrl = "";
    this.code = "";
    this.startDate = "";
    this.startTime = "";
    this.endDate = "";
    this.endTime = "";
    this.teaser = "";
    this.fileId = "";

    this.switches = {
      discount: false,
    };
  }

  /**
   * Close the modal - simplified to always close
   */
  private closeModal = () => {
    // Prevent closing only during submission or file upload
    if (this.isSubmitting || this.isFileUploadActive) {
      return;
    }
    this.dispatchEvent(new CustomEvent("modal-close", { bubbles: true, composed: true }));
  };

  /**
   * Handles close-requested event (when user clicks close buttons)
   * Uses existing canModalClose logic to determine what to do
   */
  private handleCloseRequested = () => {
    this.closeModal();
  };

  private handleModalClose = () => {
    this.dispatchEvent(
      new CustomEvent("modal-close", {
        bubbles: true,
        composed: true,
      }),
    );
  };

  private handleSaveLink = async () => {
    // Prevent submission if save should be disabled
    if (this.isSaveDisabled) {
      return;
    }

    // Validate form before submission
    if (!this.validateForm()) {
      return;
    }

    await this.handleLinkSave();
  };

  private handleFormSubmit = (event: Event) => {
    event.preventDefault();
  };

  private handleFormKeydown = async (event: KeyboardEvent) => {
    if (event.code === "Enter") {
      // First, ensure URL has protocol if it's being edited
      const target = event.target as HTMLInputElement;
      if (target && target.name === "linkUrl" && target.value) {
        let value = target.value.trim();
        if (!value.match(/^https?:\/\//i)) {
          value = "https://" + value;
          target.value = value;
          this.linkUrl = value;
        }
      }

      // Always prevent default form submission
      event.preventDefault();

      // Check if the submit button is focused
      const modal = this.renderRoot.querySelector("wow-modal") as WowModal;
      const isSubmitButtonFocused =
        modal?.shadowRoot?.activeElement?.matches?.('wow-button[variant="primary"]') ||
        document.activeElement?.matches?.('wow-button[variant="primary"]');

      // Only submit if submit button is focused and form is valid
      if (isSubmitButtonFocused && !this.isSaveDisabled && this.validateForm()) {
        await this.handleLinkSave();
      }
    }
  };

  private handleDiscountExpandChange = (e: CustomEvent) => {
    this.switches = { ...this.switches, discount: e.detail.expanded };
  };

  private get isSaveDisabled() {
    const hasUrlValue = this.linkUrl.trim().length > 0;
    const hasTitleValue = this.linkTitle.trim().length > 0;

    // More comprehensive check
    return (
      !hasUrlValue || // No URL entered
      !hasTitleValue || // No title entered
      (this.hasUrlBeenTouched && !this.isUrlValid) || // Invalid URL after user interaction
      (!this.hasUrlBeenTouched && hasUrlValue && !this.isUrlValid) || // Invalid URL before touch (for edit mode)
      this.urlEnrichingStatus === "in_progress" || // Currently loading preview
      this.isSubmitting // Already submitting
    );
  }

  private get saveButtonText() {
    if (this.isSubmitting) {
      return this.isEditMode ? t("createLink.saving") : t("createLink.creating");
    }
    if (this.isSaveDisabled && this.urlEnrichingStatus === "in_progress") {
      return t("createLink.loadingPreview");
    }
    return t(this.isEditMode ? "editLink.saveLink" : "createLink.saveLink");
  }

  private renderUrlField() {
    return html`
      <div class="form-field">
        <wow-input
          id="url"
          @change=${this.handleUrlChange}
          @input=${this.handleUrlInput}
          @blur=${this.handleUrlBlur}
          @paste=${this.handleUrlPaste}
          name="linkUrl"
          type="url"
          .value=${this.linkUrl}
          required
          placeholder=${t("editLink.linkUrlPlaceholder")}
        ></wow-input>
        ${this.urlEnrichingStatus === "in_progress"
          ? html`
              <div class="loading-indicator">
                <ph-spinner class="spinner" size="20"></ph-spinner>
                <span>Fetching link preview...</span>
              </div>
            `
          : ""}
      </div>
    `;
  }

  private renderTitleField() {
    const isDisabled = this.urlEnrichingStatus === "in_progress";

    return html`
      <div class="form-field">
        <wow-input
          name="linkTitle"
          type="text"
          maxlength="50"
          .value=${this.linkTitle}
          @input=${(e: Event) => (this.linkTitle = (e.target as HTMLInputElement).value)}
          placeholder=${t(this.isEditMode ? "editLink.linkTitlePlaceholder" : "createLink.linkTitlePlaceholder")}
          ?disabled=${isDisabled}
          required
        ></wow-input>
      </div>
    `;
  }

  private renderThumbnailUpload() {
    const isEnriching = this.urlEnrichingStatus === "in_progress";

    return html`
      <div class="thumbnail-section">
        <file-cropper
          ${ref(this.fileCropperRef)}
          name="fileId"
          .currentFileUrl=${this.linkData?.promotion_file?.path}
          vpWidth="320"
          vpHeight="320"
          context="bio_link"
          uploadSubtitle=${t("createLink.uploadImage")}
          optionalText=${t("fileUpload.optional")}
          saveButtonText=${t("file-upload.save")}
          removeButtonText=${t("file-upload.remove")}
          uploadingText=${t("fileUpload.uploading")}
          headerText=${t("file-upload.cropFile")}
          ?disabled=${isEnriching}
          @change=${this.handleImageUpload}
          @crop-started=${this.handleFileUploadStart}
          @cancel=${this.handleFileUploadCancel}
        ></file-cropper>
      </div>
    `;
  }

  private renderSwitchList() {
    const isDisabled = this.urlEnrichingStatus === "in_progress";

    return html`
      <div class="switch-list">
        <expandible-switch
          ?isExpanded=${this.switches.discount}
          label="${t("createLink.makeDiscount")}"
          @expandedChange=${this.handleDiscountExpandChange}
          ?disabled=${isDisabled}
        >
          <div slot="content">
            <wow-input
              name="code"
              placeholder="${t("discounts.form.code")}"
              required
              .value=${this.code}
              @input=${(e: Event) => (this.code = (e.target as HTMLInputElement).value)}
              ?disabled=${isDisabled}
            ></wow-input>
            <wow-textarea
              name="teaser"
              rows="4"
              maxLength="150"
              placeholder="${t("createLink.teaserText")}"
              .value=${this.teaser}
              @input=${(e: Event) => (this.teaser = (e.target as HTMLTextAreaElement).value)}
              ?disabled=${isDisabled}
            ></wow-textarea>
            <wow-input
              name="startDate"
              placeholder="${t("createLink.startDate")}"
              type="date"
              .value=${this.startDate}
              @input=${(e: Event) => {
                this.startDate = (e.target as HTMLInputElement).value;
                if (!this.startDate) {
                  this.startTime = "";
                } else if (!this.startTime) {
                  this.startTime = "00:00";
                }
              }}
              ?disabled=${isDisabled}
            ></wow-input>
            ${this.startDate
              ? html`
                  <wow-input
                    name="startTime"
                    placeholder="${t("createLink.startTime")}"
                    type="time"
                    .value=${this.startTime}
                    @input=${(e: Event) => (this.startTime = (e.target as HTMLInputElement).value)}
                    ?disabled=${isDisabled}
                  ></wow-input>
                `
              : null}
            <wow-input
              name="endDate"
              placeholder="${t("createLink.endDate")}"
              type="date"
              min=${ifDefined(this.startDate)}
              .value=${this.endDate}
              @input=${(e: Event) => {
                this.endDate = (e.target as HTMLInputElement).value;
                if (!this.endDate) {
                  this.endTime = "";
                } else if (!this.endTime) {
                  this.endTime = "23:59";
                }
              }}
              ?disabled=${isDisabled}
            ></wow-input>
            ${this.endDate
              ? html`
                  <wow-input
                    name="endTime"
                    placeholder="${t("createLink.endTime")}"
                    type="time"
                    .value=${this.endTime}
                    @input=${(e: Event) => (this.endTime = (e.target as HTMLInputElement).value)}
                    ?disabled=${isDisabled}
                  ></wow-input>
                `
              : null}
          </div>
        </expandible-switch>
      </div>
    `;
  }

  private renderFields() {
    // Always render all form fields, regardless of URL status
    return html`
      ${this.renderTitleField()} ${this.renderSwitchList()} ${this.renderPremiumControls()}
      ${this.renderThumbnailUpload()}
    `;
  }

  private renderPremiumControls() {
    const features = this.featureFlagsQuery.result?.data?.features ?? [];
    const hasSubscription = features.includes("subscription");
    const hasOneTimePurchase = features.includes("one-time-purchase");

    return featureFlag(
      hasSubscription || hasOneTimePurchase,
      html`
        <div class="form-field premium-section">
          <premium-content-controls
            .contentType=${"link"}
            .currentTier=${this.premiumTier}
            .hasTier=${!!this.subscriptionTierQuery.result.data?.tier}
            .oneTimePurchaseEnabled=${this.enableOneTimePurchase}
            .oneTimePriceCents=${this.oneTimePriceCents}
            .hasSubscriptionFlag=${hasSubscription}
            .hasOneTimePurchaseFlag=${hasOneTimePurchase}
            @tier-change=${this.handleTierChange}
            @one-time-purchase-change=${this.handleOneTimePurchaseChange}
          ></premium-content-controls>
        </div>
      `,
    );
  }

  private renderFormFields() {
    if (this.isEditMode && this.fetchingEditLinkData) {
      return html`
        <skeleton-rect class="skeleton-rectangle" width="100%" height="44px"></skeleton-rect>
        <skeleton-rect class="skeleton-rectangle" width="100%" height="56px"></skeleton-rect>
        <skeleton-rect class="skeleton-square" width="100%" height="56px"></skeleton-rect>
        <skeleton-rect class="skeleton-square" width="100%" height="192px"></skeleton-rect>
        <skeleton-rect class="skeleton-rectangle" width="100%" height="56px"></skeleton-rect>
      `;
    }
    return html`
      <div class="caption">${t("createLink.subtitle")}</div>
      ${this.renderUrlField()} ${this.renderFields()}
    `;
  }

  protected override render() {
    return html`
      <wow-modal
        variant="responsive"
        mobile-fullscreen
        ?disable-primary-button=${this.isSaveDisabled}
        @close=${this.handleModalClose}
        @footer-button-clicked=${this.handleSaveLink}
        @close-requested=${this.handleCloseRequested}
      >
        <span slot="header">${t(this.isEditMode ? "editLink.title" : "createLink.title")}</span>
        <span slot="footer-button-text">${this.saveButtonText}</span>

        <div class="modal-content">
          <form class="link-form" novalidate @submit=${this.handleFormSubmit} @keydown=${this.handleFormKeydown}>
            <div class="form-layout">${this.renderFormFields()}</div>
          </form>
        </div>
      </wow-modal>
    `;
  }
}
